/**
 * B000223バッチ番号照会
 */
import { Column, Query } from "@material-table/core";
import {
  msgState,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import {} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, SDialogContext, useFocus } from "states";
import {
  B000223Find,
  B000223FindRequest,
  catchRestError,
} from "../../clients/rest";
import { mapIds } from "../../services";
import { useNavigate } from "react-router-dom";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  batchNoFrom: string;
  batchNoTo: string;
  name: string;
}>;
// テーブルデータ定義
type Data = Partial<{
  gyo: number;
  batchNo: number;
  batchNm: string;
  pickingMethod: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  batchNoFrom: "",
  batchNoTo: "",
  name: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "3rem", align: "right" },
  { field: "batchNo", title: "バッチ番号", width: "8rem" },
  { field: "batchNm", title: "バッチ名称", width: "25rem" },
  { field: "pickingMethod", hidden: true },
];

const focusId = "B000223";

// B000223 バッチ番号照会
export const B000223 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  //テーブルの取得用Ref
  const tableRef = createRef();
  //　初回検索用使用フラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログ開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    focus(1);
  });

  //　検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  //検索用イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000223FindRequest = {
        batchNoFrom: fields.batchNoFrom,
        batchNoTo: fields.batchNoTo,
        name: fields.name,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        sort: ["batchNo,asc"],
      };
      // API呼出し
      B000223Find(param)
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="バッチ番号照会"
        gridTemplateColumns="7rem 4rem 1rem 4rem 16rem 1rem 1rem"
      >
        <SLabel>バッチ番号</SLabel>
        <SNumberField name="batchNoFrom" size={4} {...register(1)} />
        <SText>～</SText>
        <SNumberField
          name="batchNoTo"
          gridColumn={4}
          size={4}
          {...register(2)}
        />
        <SLabel gridColumn="1">バッチ名称</SLabel>
        <STextField
          name="name"
          gridColumn="span 4"
          size={40}
          {...register(3)}
        />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          {...register(4)}
          gridColumn="7"
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          width="48rem"
        />
      </SFrame>
    </FormProvider>
  );
};
